"use client";

import { useAccount, useWallet } from "@getpara/react-sdk";
import { Connection } from "@solana/web3.js";
import { useMemo } from "react";
import { useAccount as useWagmiAccount } from "wagmi";
import { getSolanaRpcUrl } from "@/lib/rpc";
import { useTokenStore } from "@/lib/zustand";
import { ChainType } from "@lifi/sdk";

const SOLANA_RPC_URL = getSolanaRpcUrl();

export function useParaAccount() {
  const { isConnected } = useAccount();
  const { data: wallet } = useWallet();
  const { external } = useAccount();
  const { address: wagmiAddress } = useWagmiAccount();
  const { fromChain } = useTokenStore();

  // Memoize the connection to avoid creating new instances on every render
  const connection = useMemo(() => new Connection(SOLANA_RPC_URL), []);

  const isExternalSolana = useMemo(() => {
    return wallet?.type === "SOLANA" && external?.solana?.isConnected;
  }, [wallet?.type, external?.solana?.isConnected]);

  const isEmailOrSocialWallet = useMemo(() => {
    const paraWallet = wallet as
      | { email?: string | null; userId?: string | null; externalId?: string | null }
      | undefined;

    return !!(paraWallet?.email || paraWallet?.userId || paraWallet?.externalId);
  }, [wallet]);

  const isExternalEvm = useMemo(() => {
    return (
      !!wagmiAddress &&
      wallet?.type === "EVM" &&
      !isEmailOrSocialWallet &&
      external?.evm?.isConnected
    );
  }, [
    wagmiAddress,
    wallet?.type,
    isEmailOrSocialWallet,
    external?.evm?.isConnected,
  ]);

  // Memoize address and caipAddress derivation
  const { address, caipAddress } = useMemo(() => {
    // When user has toggled to EVM and Phantom has wagmi EVM connected, use that
    const isDesiredEVM = fromChain?.chainType !== ChainType.SVM;

    if (isDesiredEVM && wagmiAddress) {
      return {
        address: wagmiAddress as string,
        caipAddress: `eip155:1:${wagmiAddress}`,
      };
    }

    const addr = wallet?.address;
    let caip: string | undefined;

    if (wallet?.type === "EVM") {
      caip = `eip155:1:${addr}`;
    } else if (wallet?.type === "SOLANA") {
      caip = `solana:${addr}`;
    }

    return { address: addr, caipAddress: caip };
  }, [wallet?.address, wallet?.type, wagmiAddress, fromChain?.chainType]);

  // Consider connected if Para session is active OR if wagmi has an EVM address (Phantom EVM)
  const isDesiredEVM = fromChain?.chainType !== ChainType.SVM;
  const effectiveIsConnected = isConnected || (isDesiredEVM && !!wagmiAddress);

  return useMemo(
    () => ({
      address,
      caipAddress,
      isConnected: effectiveIsConnected,
      isExternalSolana,
      isExternalEvm,
      connection,
      walletType: wallet?.type,
    }),
    [
      address,
      caipAddress,
      effectiveIsConnected,
      isExternalSolana,
      isExternalEvm,
      connection,
      wallet?.type,
    ]
  );
}
